import { Container, Heading, Text, List, ListItem, Link, Box, useColorModeValue, VStack, Divider } from '@chakra-ui/react';
import MainLayout from '../../components/layout/MainLayout';

const PrivacyPolicyPage: React.FC = () => {
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.900', 'gray.100');
  const boxBg = useColorModeValue('blue.50', 'blue.900');
  const boxText = useColorModeValue('blue.900', 'blue.100');

  return (
    <MainLayout>
      <Container maxW="3xl" py={8}>
        <VStack align="stretch" spacing={6}>
          <Heading as="h1" size="xl" mb={2} color={headingColor}>Zásady ochrany osobních údajů</Heading>
          <Text fontSize="sm" color={textColor}>
            Poslední aktualizace: {new Date().toLocaleDateString('cs-CZ')}
          </Text>
          
          <Box bg={boxBg} p={4} borderRadius="md">
            <Text fontWeight="bold" mb={2} color={boxText}>Důležité upozornění</Text>
            <Text fontSize="sm" color={boxText}>
              Respektujeme vaše soukromí. Tato stránka popisuje, jak zpracováváme vaše osobní údaje v souladu s platnými právními předpisy (zejména nařízením GDPR - Nařízení Evropského parlamentu a Rady (EU) 2016/679).
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>1. Správce osobních údajů</Heading>
            <Text color={textColor} mb={2}>
              Správcem osobních údajů je provozovatel těchto webových stránek. Kontaktní údaje správce naleznete na stránce <Link href="/kontakt" color="brand.primary">Kontakt</Link>.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>2. Jaké osobní údaje zpracováváme</Heading>
            <Text color={textColor} mb={2}>Zpracováváme následující kategorie osobních údajů:</Text>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Kontaktní údaje:</strong> jméno, příjmení, e-mailová adresa, telefonní číslo - poskytnuté dobrovolně prostřednictvím kontaktního formuláře nebo při registraci k newsletteru</ListItem>
              <ListItem><strong>IP adresa:</strong> automaticky zaznamenávaná při návštěvě webu pro účely bezpečnosti, analýzy návštěvnosti a prevence zneužití</ListItem>
              <ListItem><strong>Technické údaje:</strong> informace o zařízení, prohlížeči, operačním systému, datum a čas návštěvy, navštívené stránky</ListItem>
              <ListItem><strong>Cookies:</strong> soubory ukládané do vašeho zařízení (viz samostatná <Link href="/pravidla-cookies" color="brand.primary">Pravidla používání cookies</Link>)</ListItem>
              <ListItem><strong>Analytické údaje:</strong> anonymizovaná data o chování na webu prostřednictvím analytických nástrojů</ListItem>
            </List>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>3. Zdroje dat a vlastnictví obsahu</Heading>
            <Box bg={boxBg} p={4} borderRadius="md" mb={4}>
              <Text fontWeight="bold" mb={2} color={boxText}>Důležité právní upozornění</Text>
              <Text fontSize="sm" color={boxText}>
                Tento web zobrazuje data z externích zdrojů, která NEVLASTNÍME a která jsou majetkem příslušných organizací.
              </Text>
            </Box>
            <List spacing={3} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem>
                <strong>Data FAČR (Fotbalová asociace České republiky):</strong> Veškeré informace o zápasech, tabulkách, soutěžích a výsledcích pocházejí z veřejných API a webových stránek FAČR (<Link href="https://www.fotbal.cz" isExternal color="brand.primary">www.fotbal.cz</Link>). Tato data jsou majetkem FAČR a zobrazujeme je pouze pro informační účely v souladu s veřejnou dostupností.
              </ListItem>
              <ListItem>
                <strong>Loga klubů a týmů:</strong> Loga zobrazená na webu jsou majetkem příslušných fotbalových klubů a organizací. Používáme je pouze pro identifikační účely v rámci sportovního zpravodajství.
              </ListItem>
              <ListItem>
                <strong>Fotografie a mediální obsah:</strong> Fotografie a videa mohou pocházet z různých zdrojů včetně veřejných galerií, YouTube a dalších platforem. Veškerá autorská práva náleží jejich původním autorům.
              </ListItem>
            </List>
            <Text fontSize="sm" color={textColor}>
              Pokud jste vlastníkem obsahu a máte námitky proti jeho použití, prosím kontaktujte nás prostřednictvím <Link href="/kontakt" color="brand.primary">kontaktního formuláře</Link>.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>4. Účel zpracování</Heading>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Komunikace:</strong> Vyřízení vašich dotazů, požadavků a zpětné vazby</ListItem>
              <ListItem><strong>Newsletter:</strong> Zasílání novinek a informací (pouze se souhlasem)</ListItem>
              <ListItem><strong>Bezpečnost webu:</strong> Ochrana před útoky, prevence zneužití, detekce podvodů</ListItem>
              <ListItem><strong>Analýza návštěvnosti:</strong> Zlepšování uživatelského zážitku a optimalizace obsahu</ListItem>
              <ListItem><strong>Plnění právních povinností:</strong> Dodržování zákonných požadavků</ListItem>
            </List>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>5. Právní základ zpracování</Heading>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Souhlas (čl. 6 odst. 1 písm. a GDPR):</strong> Pro newsletter a analytické cookies</ListItem>
              <ListItem><strong>Oprávněný zájem (čl. 6 odst. 1 písm. f GDPR):</strong> Bezpečnost webu, prevence zneužití, anonymizovaná analytika</ListItem>
              <ListItem><strong>Plnění smlouvy (čl. 6 odst. 1 písm. b GDPR):</strong> Poskytování služeb</ListItem>
            </List>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>6. Doba uchovávání údajů</Heading>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Kontaktní údaje:</strong> Po dobu nezbytnou pro vyřízení požadavku, maximálně 3 roky</ListItem>
              <ListItem><strong>IP adresy:</strong> 90 dní pro účely bezpečnosti a analýzy</ListItem>
              <ListItem><strong>Analytické údaje:</strong> Anonymizovaně až 26 měsíců</ListItem>
              <ListItem><strong>Newsletter:</strong> Do odvolání souhlasu</ListItem>
            </List>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>7. Předávání údajů třetím stranám</Heading>
            <Text color={textColor} mb={2}>Vaše osobní údaje můžeme předávat:</Text>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Poskytovatelé hostingu:</strong> Pro technický provoz webu</ListItem>
              <ListItem><strong>Analytické nástroje:</strong> Pro měření návštěvnosti (anonymizovaně)</ListItem>
              <ListItem><strong>E-mailové služby:</strong> Pro zasílání newsletterů</ListItem>
            </List>
            <Text fontSize="sm" color={textColor}>
              Všichni zpracovatelé jsou pečlivě vybráni a zavázáni k ochraně vašich údajů v souladu s GDPR.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>8. Vaše práva</Heading>
            <Text color={textColor} mb={2}>Máte následující práva:</Text>
            <List spacing={2} mb={4} styleType="disc" pl={6} color={textColor}>
              <ListItem><strong>Právo na přístup:</strong> Můžete požádat o informace o zpracování vašich údajů</ListItem>
              <ListItem><strong>Právo na opravu:</strong> Můžete požadovat opravu nepřesných údajů</ListItem>
              <ListItem><strong>Právo na výmaz:</strong> Můžete požadovat smazání vašich údajů („právo být zapomenut")</ListItem>
              <ListItem><strong>Právo na omezení zpracování:</strong> Můžete požadovat omezení zpracování</ListItem>
              <ListItem><strong>Právo na přenositelnost:</strong> Můžete požádat o předání údajů</ListItem>
              <ListItem><strong>Právo vznést námitku:</strong> Můžete se bránit zpracování na základě oprávněného zájmu</ListItem>
              <ListItem><strong>Právo odvolat souhlas:</strong> Kdykoliv můžete odvolat souhlas se zpracováním</ListItem>
            </List>
            <Text color={textColor} mb={4}>
              Pro uplatnění vašich práv nás kontaktujte prostřednictvím stránky <Link href="/kontakt" color="brand.primary">Kontakt</Link>.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>9. Bezpečnost údajů</Heading>
            <Text color={textColor} mb={4}>
              Používáme přiměřená technická a organizační opatření k ochraně vašich osobních údajů před neoprávněným přístupem, ztrátou, zničením nebo změnou. Data jsou přenášena šifrovaně prostřednictvím HTTPS protokolu.
            </Text>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>10. Stížnosti</Heading>
            <Text color={textColor} mb={4}>
              Pokud se domníváte, že s vašimi údaji není nakládáno v souladu se zákonem, máte právo podat stížnost u dozorového úřadu:
            </Text>
            <Box bg={boxBg} p={4} borderRadius="md">
              <Text fontWeight="bold" color={boxText}>Úřad pro ochranu osobních údajů</Text>
              <Text fontSize="sm" color={boxText}>Pplk. Sochora 27, 170 00 Praha 7</Text>
              <Text fontSize="sm" color={boxText}>Web: <Link href="https://www.uoou.cz" isExternal color="brand.primary">www.uoou.cz</Link></Text>
            </Box>
          </Box>

          <Box>
            <Heading as="h2" size="md" mt={4} mb={3} color={headingColor}>11. Změny těchto zásad</Heading>
            <Text color={textColor}>
              Vyhrazujeme si právo tyto zásady aktualizovat. O významných změnách vás budeme informovat prostřednictvím webu.
            </Text>
          </Box>
        </VStack>
      </Container>
    </MainLayout>
  );
};

export default PrivacyPolicyPage;
